import type { ProblemDetail } from "./types";

/** Async provider of per-request auth headers (gateway assertion + correlation id). */
export type AuthHeaderProvider = () => Promise<Record<string, string>>;

export interface HttpClientConfig {
  /** Base URL for the API, e.g. "/api/trado/api/v1" (same-origin via dev/nginx proxy). */
  baseUrl: string;
  /** Supplies auth headers freshly for every request. */
  getAuthHeaders: AuthHeaderProvider;
}

/** A structured API error carrying the HTTP status and any RFC 7807 problem body. */
export class ApiError extends Error {
  readonly status: number;
  readonly problem?: ProblemDetail;

  constructor(status: number, message: string, problem?: ProblemDetail) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.problem = problem;
  }

  get isUnauthorized(): boolean {
    return this.status === 401 || this.status === 403;
  }

  get isNotFound(): boolean {
    return this.status === 404;
  }

  get isConflict(): boolean {
    return this.status === 409;
  }
}

export interface HttpClient {
  get<T>(path: string): Promise<T>;
  post<T>(path: string, body?: unknown): Promise<T>;
  put<T>(path: string, body?: unknown): Promise<T>;
  patch<T>(path: string, body?: unknown): Promise<T>;
  del<T>(path: string): Promise<T>;
}

export function createHttpClient(config: HttpClientConfig): HttpClient {
  async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
    const authHeaders = await config.getAuthHeaders();
    const headers: Record<string, string> = {
      Accept: "application/json, application/problem+json",
      ...authHeaders,
    };
    if (body !== undefined) {
      headers["Content-Type"] = "application/json";
    }

    let response: Response;
    try {
      response = await fetch(`${config.baseUrl}${path}`, {
        method,
        headers,
        body: body === undefined ? undefined : JSON.stringify(body),
      });
    } catch (cause) {
      throw new ApiError(0, "Network error — is the backend reachable?", undefined);
    }

    if (response.status === 204) {
      return undefined as T;
    }

    const text = await response.text();
    const parsed = text ? safeJson(text) : undefined;

    if (!response.ok) {
      const problem = parsed as ProblemDetail | undefined;
      const message =
        problem?.detail || problem?.title || `Request failed with status ${response.status}`;
      throw new ApiError(response.status, message, problem);
    }

    return parsed as T;
  }

  return {
    get: (path) => request("GET", path),
    post: (path, body) => request("POST", path, body),
    put: (path, body) => request("PUT", path, body),
    patch: (path, body) => request("PATCH", path, body),
    del: (path) => request("DELETE", path),
  };
}

function safeJson(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return undefined;
  }
}
